import React, { useState, useContext, createContext, useReducer, useRef, useEffect } from "react";
import { generateId } from "../utils/genIds";

const newDueLevel = {
  id: null,
  objectId: null,
  name: "",
  description: "",
  amount: "",
  destroy: false,
  status: "active",
  isNew: true, // Track new items
};

const initialState = {
  fund: {},
  dueLevels: [],
  hasChanges: false,
};

const dueLevelsReducer = (state, action) => {
  switch (action.type) {
    case "ADD_DUE_LEVEL":
      return {
        ...state,
        dueLevels: [action.dueLevel, ...state.dueLevels], // Add to TOP
        hasChanges: true,
      };
    case "SET_DUE_LEVELS":
      return { ...state, dueLevels: action.dueLevels, hasChanges: true };
    case "MARK_CHANGES":
      return { ...state, hasChanges: true };
    default:
      return state;
  }
};

const DueLevelsContext = createContext(null);

const useDueLevels = () => {
  const [state, dispatch] = useContext(DueLevelsContext);

  const addDueLevel = () => {
    dispatch({
      type: "ADD_DUE_LEVEL",
      dueLevel: { ...newDueLevel, objectId: generateId() },
    });
  };

  const removeDueLevel = (dueLevel) => {
    if (dueLevel.id === null) {
      // New item - just remove it
      const newduelevels = state.dueLevels.filter(
        (dl) => dl.objectId != dueLevel.objectId
      );
      dispatch({ type: "SET_DUE_LEVELS", dueLevels: newduelevels });
    } else {
      // Existing item - mark for destruction
      const newduelevels = state.dueLevels.map((dl) => {
        if (dl.objectId === dueLevel.objectId) {
          return { ...dl, destroy: true };
        } else {
          return { ...dl };
        }
      });
      dispatch({ type: "SET_DUE_LEVELS", dueLevels: newduelevels });
    }
  };

  const undoRemove = (dueLevel) => {
    const newduelevels = state.dueLevels.map((dl) => {
      if (dl.objectId === dueLevel.objectId) {
        return { ...dl, destroy: false };
      } else {
        return { ...dl };
      }
    });
    dispatch({ type: "SET_DUE_LEVELS", dueLevels: newduelevels });
  };

  return { state, dispatch, addDueLevel, removeDueLevel, undoRemove };
};

const DueLevelsProvider = (props) => {
  const [state, dispatch] = useReducer(dueLevelsReducer, {
    ...initialState,
    fund: props.fund,
    dueLevels: (props.dueLevels || []).map(dl => ({ ...dl, isNew: false })),
    dueLevelStatuses: props.dueLevelStatuses,
    intervalOptions: props.intervalOptions,
    archiveUrlTemplate: props.archiveUrlTemplate,
    canArchive: props.canArchive,
  });

  const value = React.useMemo(() => [state, dispatch], [state]);

  return <DueLevelsContext.Provider value={value} {...props} />;
};

// Pending Removal Card - shows when an existing item is marked for deletion
const PendingRemovalCard = ({ dueLevel, index }) => {
  const { undoRemove } = useDueLevels();
  const namePrefix = `fund[due_levels_attributes][${index}]`;

  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
      <input type="hidden" name={`${namePrefix}[_destroy]`} value="1" />
      <input type="hidden" name={`${namePrefix}[id]`} value={dueLevel.id} />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </div>
          <div>
            <p className="font-medium text-red-800">
              "{dueLevel.name}" will be removed
            </p>
            <p className="text-sm text-red-600">
              This change will take effect when you save
            </p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            undoRemove(dueLevel);
          }}
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
          </svg>
          Undo
        </button>
      </div>
    </div>
  );
};

const DueLevelFields = (props) => {
  const { removeDueLevel, state } = useDueLevels();
  const { dueLevelStatuses, intervalOptions, archiveUrlTemplate, canArchive } = state;
  const { dueLevel, index, isFirst } = props;
  const [recurring, setRecurring] = useState(dueLevel.recurring);
  const [isCollapsed, setIsCollapsed] = useState(!dueLevel.isNew); // New items start expanded
  const namePrefix = `fund[due_levels_attributes][${index}]`;
  const cardRef = useRef(null);

  // Scroll into view and highlight when newly added
  useEffect(() => {
    if (dueLevel.isNew && isFirst && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const handleRemoveClick = (e) => {
    e.preventDefault();
    removeDueLevel(dueLevel);
  };

  const handleSetRecurring = (e) => {
    const { checked } = e.target;
    setRecurring(checked);
  };

  // Show pending removal state for existing items marked for deletion
  if (dueLevel.destroy) {
    return <PendingRemovalCard dueLevel={dueLevel} index={index} />;
  }

  return (
    <div
      ref={cardRef}
      className={`bg-white border rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${
        dueLevel.isNew
          ? 'border-indigo-300 ring-2 ring-indigo-100'
          : 'border-gray-200'
      }`}
    >
      {dueLevel.id && (
        <input type="hidden" name={`${namePrefix}[id]`} value={dueLevel.id} />
      )}

      {/* Collapsed Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 border-b cursor-pointer transition-colors ${
          dueLevel.isNew
            ? 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
        }`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${dueLevel.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">
              {dueLevel.name || 'New Payment Option'}
            </span>
            {dueLevel.amount && (
              <span className="text-sm text-gray-500">
                ${parseFloat(dueLevel.amount).toFixed(2)}
                {recurring && dueLevel.interval && ` / ${dueLevel.interval}`}
              </span>
            )}
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2">
            {dueLevel.isNew && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 animate-pulse">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                New - Unsaved
              </span>
            )}
            {recurring && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Recurring
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {!isCollapsed && (
        <div className="p-4 space-y-4">
          {/* Name and Amount Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Option Name <span className="text-red-500">*</span>
              </label>
              <input
                name={`${namePrefix}[name]`}
                defaultValue={dueLevel.name}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
                placeholder="e.g., Monthly Dues, Annual Membership"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  name={`${namePrefix}[amount]`}
                  defaultValue={dueLevel.amount}
                  type="number"
                  step="0.01"
                  min="0"
                  className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name={`${namePrefix}[status]`}
                defaultValue={dueLevel.status}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white transition-colors"
              >
                {dueLevelStatuses.map((dl) => (
                  <option key={dl} value={dl}>
                    {dl.charAt(0).toUpperCase() + dl.slice(1)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Inactive options won't be shown to members
              </p>
            </div>
          </div>

          {/* Recurring Section */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start gap-3">
              <div className="flex items-center h-5 mt-0.5">
                <input type="hidden" name={`${namePrefix}[recurring]`} value="false" />
                <input
                  id={`${dueLevel.objectId}Recurring`}
                  type="checkbox"
                  name={`${namePrefix}[recurring]`}
                  checked={recurring}
                  onChange={handleSetRecurring}
                  disabled={dueLevel.id !== null}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor={`${dueLevel.objectId}Recurring`}
                  className={`block text-sm font-medium ${dueLevel.id !== null ? 'text-gray-500' : 'text-gray-900'}`}
                >
                  Enable Recurring Payments (Auto-pay)
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  {dueLevel.id !== null
                    ? "Cannot be changed after saving. Create a new option for different billing."
                    : "Members can subscribe to automatic recurring payments"
                  }
                </p>

                {recurring && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Billing Frequency
                    </label>
                    <select
                      name={`${namePrefix}[interval]`}
                      defaultValue={dueLevel.interval || 'monthly'}
                      disabled={dueLevel.id !== null}
                      className="block w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {Object.keys(intervalOptions).map((key) => (
                        <option key={key} value={key}>
                          {key.charAt(0).toUpperCase() + key.slice(1).replace('-', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-2 border-t border-gray-200">
            {/* Discard Button - only for new unsaved due levels */}
            {dueLevel.isNew && (
              <button
                onClick={handleRemoveClick}
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                Discard
              </button>
            )}

            {/* Archive Button - only for saved due levels */}
            {canArchive && dueLevel.id && !dueLevel.isNew && (
              <a
                href={archiveUrlTemplate?.replace('__ID__', dueLevel.id)}
                data-method="patch"
                data-confirm={`Are you sure you want to archive "${dueLevel.name}"? It will be hidden from the public page but existing subscriptions and transactions will be preserved.`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                </svg>
                Archive
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const DueLevelsContainer = () => {
  const { state } = useDueLevels();
  const activeLevels = state.dueLevels.filter(dl => !dl.destroy);
  const pendingRemovals = state.dueLevels.filter(dl => dl.destroy);

  if (state.dueLevels.length === 0) {
    return (
      <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No payment options</h3>
        <p className="mt-1 text-sm text-gray-500">Get started by adding your first payment option.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {state.dueLevels.map((dueLevel, index) => (
        <DueLevelFields
          key={dueLevel.objectId}
          dueLevel={dueLevel}
          index={index}
          isFirst={index === 0}
        />
      ))}
    </div>
  );
};

// Unsaved Changes Banner
const UnsavedChangesBanner = () => {
  const { state } = useDueLevels();

  const newCount = state.dueLevels.filter(dl => dl.isNew && !dl.destroy).length;
  const removedCount = state.dueLevels.filter(dl => dl.destroy).length;

  if (newCount === 0 && removedCount === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
        </svg>
        <span className="text-sm font-medium text-amber-800">
          You have unsaved changes:
          {newCount > 0 && <span className="ml-1">{newCount} new option{newCount > 1 ? 's' : ''}</span>}
          {newCount > 0 && removedCount > 0 && <span>,</span>}
          {removedCount > 0 && <span className="ml-1">{removedCount} pending removal{removedCount > 1 ? 's' : ''}</span>}
        </span>
      </div>
      <span className="text-xs text-amber-600">
        Save your changes below
      </span>
    </div>
  );
};

const AddDueLevel = () => {
  const { addDueLevel } = useDueLevels();

  const handleClick = (e) => {
    e.preventDefault();
    addDueLevel();
  };

  return (
    <button
      onClick={handleClick}
      type="button"
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
      </svg>
      Add Payment Option
    </button>
  );
};

const NestedDueLevelsFields = ({
  fund,
  dueLevels,
  dueLevelStatuses,
  intervalOptions,
  archiveUrlTemplate,
  canArchive,
}) => {
  return (
    <DueLevelsProvider
      fund={fund}
      dueLevels={dueLevels}
      dueLevelStatuses={dueLevelStatuses}
      intervalOptions={intervalOptions}
      archiveUrlTemplate={archiveUrlTemplate}
      canArchive={canArchive}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b border-gray-200">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Payment Options
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Define the payment amounts your members can choose from
            </p>
          </div>
          <AddDueLevel />
        </div>

        {/* Unsaved Changes Banner */}
        <UnsavedChangesBanner />

        {/* Custom Amount Toggle */}
        <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-100 rounded-lg">
          <div className="flex items-center h-5 mt-0.5">
            <input type="hidden" name="fund[allow_custom_amount]" value="false" />
            <input
              id="fund_allow_custom_amount"
              type="checkbox"
              name="fund[allow_custom_amount]"
              defaultChecked={fund.allow_custom_amount}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
          </div>
          <div>
            <label
              htmlFor="fund_allow_custom_amount"
              className="block text-sm font-medium text-gray-900"
            >
              Allow Custom Amounts
            </label>
            <p className="text-xs text-gray-600 mt-0.5">
              Let members enter their own payment amount in addition to the options below
            </p>
          </div>
        </div>

        {/* Due Levels List */}
        <DueLevelsContainer />

        {/* Helper Text */}
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          Remember to save your changes at the bottom of the page
        </p>
      </div>
    </DueLevelsProvider>
  );
};

export default NestedDueLevelsFields;
